import React, { useEffect, useState } from 'react';
import { MessageSquareHeart, ClipboardList, BarChart3, Settings2, ChevronLeft } from 'lucide-react';
import { atlasApi, AtlasUser } from '../shared/atlasApi';
import MinhasPendencias from './MinhasPendencias';
import Responder from './Responder';
import MeusRelatorios from './MeusRelatorios';
import Relatorio from './Relatorio';
import GerenciarCiclos from './GerenciarCiclos';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
  isAdmin: boolean;
}

type View = 'home' | 'pendencias' | 'responder' | 'relatorios' | 'relatorio' | 'ciclos';

const Card: React.FC<{ icon: React.ReactNode; titulo: string; descricao: string; cor: string; badge?: number; onClick: () => void }> = ({ icon, titulo, descricao, cor, badge, onClick }) => (
  <button onClick={onClick} className="relative text-left bg-white rounded-2xl border border-gray-200 p-6 hover:border-purple-300 hover:shadow-md transition flex flex-col gap-3">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${cor}`}>{icon}</div>
    <div>
      <h3 className="font-bold text-gray-900">{titulo}</h3>
      <p className="text-sm text-gray-500 mt-1">{descricao}</p>
    </div>
    {badge != null && badge > 0 && <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full text-xs font-bold bg-rose-500 text-white">{badge}</span>}
  </button>
);

const FeedbackApp: React.FC<Props> = ({ user, podeEditar, isAdmin }) => {
  const [view, setView] = useState<View>('home');
  const [pendCount, setPendCount] = useState<number>(0);
  const [sel, setSel] = useState<{ cicloId?: string; funccodi?: number; souEu?: boolean }>({});

  useEffect(() => {
    atlasApi.minhasPendencias(user).then(r => setPendCount(r.total || 0)).catch(() => {});
  }, [user, view]);

  const voltar = () => setView('home');
  const Voltar = () => (
    <button onClick={voltar} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 mb-4"><ChevronLeft className="w-4 h-4" /> Voltar</button>
  );

  if (view === 'pendencias') return (
    <div className="p-6"><Voltar />
      <MinhasPendencias user={user} onResponder={(cicloId, funccodi, souEu) => { setSel({ cicloId, funccodi, souEu }); setView('responder'); }} />
    </div>
  );
  if (view === 'responder' && sel.cicloId && sel.funccodi != null) return (
    <div className="p-6"><Voltar />
      <Responder user={user} cicloId={sel.cicloId} avaliadoFunccodi={sel.funccodi} souEu={!!sel.souEu} onConcluir={() => setView('pendencias')} />
    </div>
  );
  if (view === 'relatorios') return (
    <div className="p-6"><Voltar />
      <MeusRelatorios user={user} onAbrir={(cicloId, funccodi) => { setSel({ cicloId, funccodi }); setView('relatorio'); }} />
    </div>
  );
  if (view === 'relatorio' && sel.cicloId && sel.funccodi != null) return (
    <div className="p-6"><Voltar />
      <Relatorio user={user} cicloId={sel.cicloId} funccodi={sel.funccodi} />
    </div>
  );
  if (view === 'ciclos') return (
    <div className="p-6"><Voltar />
      <GerenciarCiclos user={user} onVerRelatorio={(cicloId, funccodi) => { setSel({ cicloId, funccodi }); setView('relatorio'); }} />
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-2 text-purple-100 text-sm font-medium"><MessageSquareHeart className="w-4 h-4" /> Avaliação por pares</div>
        <h2 className="text-2xl font-bold mt-1">Feedback 360</h2>
        <p className="text-purple-100 text-sm mt-1 max-w-xl">Avalie colegas, receba feedback e acompanhe sua evolução. As respostas são agregadas sem identificar os avaliadores.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card icon={<ClipboardList className="w-6 h-6" />} titulo="Minhas pendências" descricao="Avaliações que você precisa responder." cor="bg-rose-500" badge={pendCount} onClick={() => setView('pendencias')} />
        <Card icon={<BarChart3 className="w-6 h-6" />} titulo="Meus relatórios" descricao="Resultados dos ciclos em que você foi avaliado." cor="bg-emerald-500" onClick={() => setView('relatorios')} />
        {(isAdmin || podeEditar) && (
          <Card icon={<Settings2 className="w-6 h-6" />} titulo="Gerenciar ciclos" descricao="Crie e administre campanhas de feedback." cor="bg-amber-500" onClick={() => setView('ciclos')} />
        )}
      </div>
    </div>
  );
};

export default FeedbackApp;
